/**
 * FRONT-2598 — the pure draft behind manual editing of a sketch or a deck in
 * the Canvas. No React, no DOM, no storage: the editor
 * (canvas-artifact-editor.tsx) holds one of these and every decision about it
 * lives here so it can be swept by tests.
 *
 * Block ids come from the edit view's own script (src/artifacts/editor-script.ts):
 * a sketch's are "0", "1", …; a deck's are `s<i>-<n>` where `i` is the slide's
 * ORIGINAL index. Ids are positional, so they can shift after any save — which is
 * why a draft remembers each edit's `original` html and why the editor restarts
 * from a fresh inventory after every save.
 */

import { notesPlainToHtml, notesToPlainText } from './deck-notes-text';

export interface EditEntry {
  /** The block's innerHTML when this edit started (what the page announced in `ready`). */
  original: string;
  /** The block's innerHTML now. */
  html: string;
}

/** One slide in the person's arrangement. `from` is the slide's ORIGINAL 0-based index. */
export interface SlideRow {
  from: number;
  notes: string;
  /** A duplicate made in this draft (shown as "(copy)"); the first occurrence of a `from` is never a copy. */
  copy?: boolean;
}

export interface OriginalSlide {
  html: string;
  notes: string;
}

export interface ArtifactDraft {
  /** The `updatedAt` this draft was started against. */
  base: string;
  edits: Record<string, EditEntry>;
  /** Present only while the order, the duplicates, the deletions or the notes differ from the original. */
  slides?: SlideRow[];
}

export function emptyDraft(base: string): ArtifactDraft {
  return { base, edits: {} };
}

// --- block edits -----------------------------------------------------------

/**
 * A `change` message from the page. Typing a block back to its original drops the
 * edit. An id the page never announced (`inventory`) and that has no edit yet is
 * ignored: there is no original to compare against.
 */
export function recordChange(
  draft: ArtifactDraft,
  inventory: Readonly<Record<string, string>> | null,
  id: string,
  html: string,
): ArtifactDraft {
  const original = draft.edits[id]?.original ?? inventory?.[id];
  if (original === undefined) return draft;
  const edits = { ...draft.edits };
  if (html === original) delete edits[id];
  else edits[id] = { original, html };
  return { ...draft, edits };
}

/** What the page is asked to put back after a reload: `{ id: html }`. */
export function restorePayload(draft: ArtifactDraft): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [id, e] of Object.entries(draft.edits)) out[id] = e.html;
  return out;
}

// --- slides ----------------------------------------------------------------

export function originalRows(originals: readonly OriginalSlide[]): SlideRow[] {
  return originals.map((s, from) => ({ from, notes: s.notes }));
}

/** The arrangement the strip shows: the draft's, or the original order. */
export function rowsOf(draft: ArtifactDraft, originals: readonly OriginalSlide[]): SlideRow[] {
  return draft.slides ?? originalRows(originals);
}

export function slidesDiffer(rows: readonly SlideRow[] | undefined, originals: readonly OriginalSlide[]): boolean {
  if (!rows) return false;
  if (rows.length !== originals.length) return true;
  return rows.some((r, i) => r.copy === true || r.from !== i || r.notes !== (originals[i]?.notes ?? ''));
}

/** Keep `slides` only while it differs from the original arrangement. */
function withSlides(draft: ArtifactDraft, rows: SlideRow[], originals: readonly OriginalSlide[]): ArtifactDraft {
  const { slides: _drop, ...rest } = draft;
  void _drop;
  const edits = dropEditsOfRemovedSlides(rest.edits, rows);
  return slidesDiffer(rows, originals) ? { ...rest, edits, slides: rows } : { ...rest, edits };
}

/** An edit inside a slide that was deleted has nowhere to go; drop it so it cannot keep the draft dirty. */
function dropEditsOfRemovedSlides(edits: Record<string, EditEntry>, rows: readonly SlideRow[]): Record<string, EditEntry> {
  const kept = new Set(rows.map((r) => r.from));
  const out: Record<string, EditEntry> = {};
  for (const [id, e] of Object.entries(edits)) {
    const m = /^s(\d+)-/.exec(id);
    if (m && !kept.has(Number(m[1]))) continue;
    out[id] = e;
  }
  return out;
}

export function moveSlide(
  draft: ArtifactDraft,
  originals: readonly OriginalSlide[],
  index: number,
  dir: -1 | 1,
): ArtifactDraft {
  const rows = [...rowsOf(draft, originals)];
  const to = index + dir;
  if (index < 0 || index >= rows.length || to < 0 || to >= rows.length) return draft;
  [rows[index], rows[to]] = [rows[to]!, rows[index]!];
  return withSlides(draft, rows, originals);
}

export function duplicateSlide(draft: ArtifactDraft, originals: readonly OriginalSlide[], index: number): ArtifactDraft {
  const rows = [...rowsOf(draft, originals)];
  const row = rows[index];
  if (!row) return draft;
  rows.splice(index + 1, 0, { from: row.from, notes: row.notes, copy: true });
  return withSlides(draft, rows, originals);
}

export function deleteSlide(draft: ArtifactDraft, originals: readonly OriginalSlide[], index: number): ArtifactDraft {
  const rows = [...rowsOf(draft, originals)];
  if (rows.length <= 1 || !rows[index]) return draft;
  rows.splice(index, 1);
  return withSlides(draft, rows, originals);
}

export function setSlideNotes(
  draft: ArtifactDraft,
  originals: readonly OriginalSlide[],
  index: number,
  notes: string,
): ArtifactDraft {
  const rows = [...rowsOf(draft, originals)];
  const row = rows[index];
  if (!row) return draft;
  rows[index] = { ...row, notes };
  return withSlides(draft, rows, originals);
}

/** The deck's slides once a save with this draft has been applied (used only if re-reading the deck fails). Notes go back to their stored (markup) form. */
export function slidesAfterSave(draft: ArtifactDraft, originals: readonly OriginalSlide[]): OriginalSlide[] {
  return rowsOf(draft, originals).map((r) => ({ html: originals[r.from]?.html ?? '', notes: notesPlainToHtml(r.notes) }));
}

/** The ORIGINAL slide index a deck block id (`s<i>-<n>`) belongs to; null for any other id. */
export function slideOfBlockId(id: string): number | null {
  const m = /^s(0|[1-9]\d*)-/.exec(id);
  return m ? Number(m[1]) : null;
}

/** Where, in the current arrangement, the slide that was originally `from` sits (its first occurrence); null if it was deleted. */
export function rowIndexOfOriginal(rows: readonly SlideRow[], from: number): number | null {
  const i = rows.findIndex((r) => r.from === from);
  return i === -1 ? null : i;
}

/**
 * A deck's `body` (the structure JSON `deck_read` returns) as its slides; null for anything else.
 * Notes come back as TEXT (`notesToPlainText`): that is what the strip shows and what is sent when
 * the person changes them, so "changed" is always text against text.
 */
export function parseDeckBody(body: string): OriginalSlide[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return null;
  }
  const slides = (parsed as { slides?: unknown } | null)?.slides;
  if (!Array.isArray(slides) || slides.length === 0) return null;
  const out: OriginalSlide[] = [];
  for (const s of slides) {
    if (!s || typeof s !== 'object') return null;
    const r = s as { html?: unknown; notes?: unknown };
    if (typeof r.html !== 'string') return null;
    out.push({ html: r.html, notes: typeof r.notes === 'string' ? notesToPlainText(r.notes) : '' });
  }
  return out;
}

// --- dirty + save body -----------------------------------------------------

/** Dirty iff any edit remains or the slide arrangement/notes differ. `originals` is null for a sketch. */
export function isArtifactDraftDirty(draft: ArtifactDraft, originals: readonly OriginalSlide[] | null): boolean {
  if (Object.keys(draft.edits).length > 0) return true;
  return originals !== null && slidesDiffer(draft.slides, originals);
}

export interface ManualEditRequest {
  baseUpdatedAt: string;
  edits: Record<string, string>;
  slides?: Array<{ from: number; notes?: string }>;
}

/** The PUT body. `slides` is sent only when the arrangement or notes differ from the original. */
export function buildSaveBody(
  draft: ArtifactDraft,
  originals: readonly OriginalSlide[] | null,
  baseUpdatedAt: string,
): ManualEditRequest {
  const body: ManualEditRequest = { baseUpdatedAt, edits: restorePayload(draft) };
  if (originals && draft.slides && slidesDiffer(draft.slides, originals)) {
    body.slides = draft.slides.map((r) => {
      const changed = r.notes !== (originals[r.from]?.notes ?? '');
      return changed ? { from: r.from, notes: r.notes } : { from: r.from };
    });
  }
  return body;
}

// --- re-apply after Eddy changed the artifact -------------------------------

export interface ReapplyResult {
  /** The edits that found their block, keyed by the block's id in the NEW version. */
  mapped: Record<string, EditEntry>;
  placed: number;
  total: number;
  /** The person had slide order / duplicate / delete / notes changes, which are not carried over. */
  slidesDropped: boolean;
}

/**
 * Map each edit to the ONE block of the new version whose innerHTML still equals
 * the edit's `original`. No match (Eddy rewrote that text), several matches (the
 * same text appears twice, so the edit cannot say which), or a block another edit
 * already took: the edit is not placed.
 */
export function reapplyEdits(
  draft: ArtifactDraft,
  inventory: Readonly<Record<string, string>>,
  originals: readonly OriginalSlide[] | null,
): ReapplyResult {
  const entries = Object.values(draft.edits);
  const claims = new Map<string, EditEntry[]>();
  for (const e of entries) {
    const hits = Object.keys(inventory).filter((id) => inventory[id] === e.original);
    if (hits.length === 1) claims.set(hits[0]!, [...(claims.get(hits[0]!) ?? []), e]);
  }
  // A block claimed by two edits is not unique to either: neither is placed.
  const mapped: Record<string, EditEntry> = {};
  for (const [id, list] of claims) if (list.length === 1) mapped[id] = list[0]!;
  return {
    mapped,
    placed: Object.keys(mapped).length,
    total: entries.length,
    slidesDropped: originals !== null && slidesDiffer(draft.slides, originals),
  };
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** The one-line result shown after "Re-apply my edits". */
export function reapplyMessage(r: ReapplyResult): string {
  let line: string;
  if (r.total === 0) line = 'You had no text edits to re-apply.';
  else if (r.placed === r.total) line = `Re-applied ${plural(r.total, 'your edit', `all ${r.total} edits`)}.`;
  else if (r.placed === 0) {
    line =
      r.total === 1
        ? 'Your edit could not be placed because EDDY changed that text.'
        : `None of your ${r.total} edits could be placed because EDDY changed that text.`;
  } else {
    const missed = r.total - r.placed;
    line =
      `Re-applied ${r.placed} of ${r.total} edits. ` +
      `${missed} could not be placed because EDDY changed ${plural(missed, 'that text', 'those texts')}.`;
  }
  return r.slidesDropped ? `${line} Your slide order and notes changes were dropped.` : line;
}

// --- slide titles ----------------------------------------------------------

type ParsedDoc = { querySelector(selector: string): { textContent: string | null } | null };

function defaultParse(html: string): ParsedDoc | null {
  // DOMParser builds an inert document: no scripts run and nothing is attached to Eddy's page.
  return typeof DOMParser === 'undefined' ? null : new DOMParser().parseFromString(html, 'text/html');
}

/** The first h1-h3 text of a slide's original html; "Slide N" when there is none. */
export function slideTitle(html: string, number: number, parse: (html: string) => ParsedDoc | null = defaultParse): string {
  let text = '';
  try {
    text = (parse(html)?.querySelector('h1, h2, h3')?.textContent ?? '').replace(/\s+/g, ' ').trim();
  } catch {
    text = '';
  }
  if (!text) return `Slide ${number}`;
  return text.length > 80 ? `${text.slice(0, 79)}…` : text;
}
